/**
 * Document Connections Tab Component
 * 
 * Displays all documents connected to the source document, grouped by target doctype.
 * Fully doctype-agnostic - all display logic comes from config.
 */

import React from "react";
import { useDocConnections } from "@/features/common/api/useDocConnections";
import { useRelationshipGraph } from "@/features/common/api/useRelationshipGraph";
import { 
  getConnectionColumns, 
  getDefaultColumns,
} from "./docConnectionsConfig";
import type { DocConnectionGroup, RelationshipGraphGroup, FrappeFilter } from "@/lib/erp/docs";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { buildDocRoute, buildListRoute, buildListRouteWithFilters } from "@/lib/config/docRoutes";
import { formatCurrency } from "@/lib/format/currency";
import { useLanguage } from "@/i18n/useLanguage";
import { getDoctypeLabelTranslation } from "@/lib/views/labels/translations";
import { isRelationshipGraphEnabled } from "@/features/common/flags/relationshipGraph";

interface DocConnectionsTabProps {
  doctype: string;
  name: string;
  /**
   * Optional list of connection group IDs to show.
   * If provided, only groups matching these IDs will be displayed.
   * If omitted, all groups are shown.
   * 
   * This allows DocViewSchema to control which connection groups appear.
   */
  connectionGroups?: string[];
}

/**
 * Format a value based on column type
 */
function formatValue(value: any, type?: string): string {
  if (value === null || value === undefined) return "-";
  
  switch (type) {
    case "currency":
      return formatCurrency(value);
    case "date":
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    case "status":
      return value || "-";
    default:
      return String(value);
  }
}

/**
 * Get doctype icon (placeholder - can be enhanced later)
 */
function getDoctypeIcon(_doctype: string): string {
  // Placeholder - can be enhanced with icon mapping later
  return "📄";
}

type UiGroup = {
  id: string;
  label: string;
  members: DocConnectionGroup[];
};

/**
 * Group connections by backend-provided group_id/group_label
 * 
 * Backend now provides group metadata from ERPNext dashboard.
 * This function simply organizes the flat list into UI groups.
 * 
 * Rules:
 * - If no group_id present → fall back to single "Connections" group
 * - Group by group_id, use group_label for display
 * - "Other" group always goes last
 */
function groupByBackendGroup(dataGroups: DocConnectionGroup[]): UiGroup[] {
  if (!dataGroups || dataGroups.length === 0) {
    return [];
  }

  // If backend didn't provide group_id, fall back to single group
  const hasAnyGroup = dataGroups.some(g => g.group_id);

  if (!hasAnyGroup) {
    return [
      {
        id: "default",
        label: "Connections",
        members: dataGroups,
      },
    ];
  }

  // Group by group_id
  const groupsMap = new Map<string, UiGroup>();

  for (const g of dataGroups) {
    const id = g.group_id || "other";
    const label = g.group_label || (g.group_id ? g.group_id : "Other");

    if (!groupsMap.has(id)) {
      groupsMap.set(id, { id, label, members: [] });
    }
    groupsMap.get(id)!.members.push(g);
  }

  // Sort groups: everything except "other" first, "other" last
  return Array.from(groupsMap.values()).sort((a, b) => {
    if (a.id === "other") return 1;
    if (b.id === "other") return -1;
    return 0;
  });
}

/**
 * Reusable Document Connections Tab component.
 * 
 * Displays all documents connected to the source document, grouped by ERPNext dashboard structure.
 * 
 * ERPNext Dashboard-style behavior:
 * - Always shows ALL configured doctypes from dashboard, even when count = 0
 * - Shows "No documents yet" for doctypes with 0 documents
 * - Shows table with rows when documents exist
 * 
 * Grouping is now driven by backend via ERPNext dashboard metadata.
 * Frontend only handles rendering and column configuration.
 * 
 * DocViewSchema Integration:
 * - If connectionGroups is provided, only groups matching those IDs are shown
 * - This allows schema to control which connection groups appear
 */
export function DocConnectionsTab({ doctype, name, connectionGroups }: DocConnectionsTabProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Evaluate feature flag inside component (reactive, testable, HMR-friendly)
  const useGraphFlag = isRelationshipGraphEnabled();
  
  // Base enable condition for graph query
  const baseGraphEnabled = useGraphFlag && !!doctype && !!name && name !== "new";
  
  // State latch: once we detect allowlist-filtered or graph error, disable graph query permanently
  // This prevents refetch loops and background churn
  const [allowlistFiltered, setAllowlistFiltered] = React.useState(false);
  const [graphErrored, setGraphErrored] = React.useState(false);
  
  // Graph query enabled only if base conditions met AND not allowlist-filtered AND not errored
  const graphEnabled = baseGraphEnabled && !allowlistFiltered && !graphErrored;
  
  // Conditional hook usage based on feature flag and allowlist state
  const { data: graphData, isLoading: graphLoading, error: graphError } = useRelationshipGraph(
    doctype,
    name,
    { enabled: graphEnabled }
  );

  // Legacy query enabled if feature flag is off OR allowlist-filtered OR graph errored
  const legacyEnabled = !useGraphFlag || allowlistFiltered || graphErrored;
  const { data: legacyData, isLoading: legacyLoading, error: legacyError } = useDocConnections(
    doctype,
    name,
    { enabled: legacyEnabled }
  );
  
  // Latch allowlist-filtered state after first response
  // This ensures we only make one graph request for deny-all doctypes
  React.useEffect(() => {
    if (graphData?.enabled === false && graphData?.reason === "allowlist_filtered") {
      setAllowlistFiltered(true);
    }
  }, [graphData?.enabled, graphData?.reason]);
  
  // Latch graph error state - if graph fails, fall back to legacy permanently
  React.useEffect(() => {
    if (graphError) {
      setGraphErrored(true);
    }
  }, [graphError]);
  
  // Determine which data source to use for rendering
  // Use legacy if: feature flag off, allowlist-filtered, OR graph errored
  // This ensures we always have a working data source (legacy mirrors ERPNext)
  const shouldUseLegacy = !useGraphFlag || allowlistFiltered || graphErrored;
  
  // Use graph data if enabled and not allowlist-filtered, otherwise legacy
  const data = shouldUseLegacy ? legacyData : graphData;
  const isLoading = shouldUseLegacy ? legacyLoading : graphLoading;
  const error = shouldUseLegacy ? legacyError : graphError;
  
  // Groups are already materialized - use directly
  // For graph data, groups are already in the correct format
  // For legacy data, we need to group by backend group
  const grouped = React.useMemo(() => {
    if (!data?.groups) return [];
    
    // If using graph, groups are already materialized and grouped
    if (useGraphFlag && !allowlistFiltered && !graphErrored) {
      // Graph groups are already in the format we need
      // Group by group_id for UI display
      const groupsMap = new Map<string, UiGroup>();
      
      for (const group of data.groups as RelationshipGraphGroup[]) {
        const groupId = group.group_id || "other";
        const groupLabel = group.group_label || "Other";
        
        if (!groupsMap.has(groupId)) {
          groupsMap.set(groupId, { id: groupId, label: groupLabel, members: [] });
        }
        groupsMap.get(groupId)!.members.push(group as DocConnectionGroup);
      }
      
      // Sort groups: everything except "other" first, "other" last
      let allGroups = Array.from(groupsMap.values()).sort((a, b) => {
        if (a.id === "other") return 1;
        if (b.id === "other") return -1;
        return 0;
      });
      
      // Filter by connectionGroups if specified
      if (connectionGroups && connectionGroups.length > 0) {
        allGroups = allGroups.filter(group => 
          connectionGroups.includes(group.id)
        );
      }
      
      return allGroups;
    } else {
      // Legacy path: group by backend-provided group_id/group_label
      const allGroups = groupByBackendGroup(data.groups);
      
      // If connectionGroups is specified, filter to only show those groups
      if (connectionGroups && connectionGroups.length > 0) {
        return allGroups.filter(group => 
          connectionGroups.includes(group.id)
        );
      }
      
      return allGroups;
    }
  }, [data?.groups, connectionGroups]);
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load document connections. Please try again.
        </AlertDescription>
      </Alert>
    );
  }
  
  // Updated empty state: backend will send groups even with 0 docs if dashboard exists
  const hasData = data?.groups && data.groups.length > 0;
  
  if (!hasData) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <p>No connected documents yet.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {grouped.map((group) => {
        return (
          <Card key={group.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{group.label}</CardTitle>
                {/* Optional: show group total count */}
                {/* <Badge variant="secondary">
                  {group.members.reduce((sum, m) => sum + m.count, 0)} document
                  {group.members.reduce((sum, m) => sum + m.count, 0) !== 1 ? "s" : ""}
                </Badge> */}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {group.members.map((targetGroup) => {
                const columns = 
                  getConnectionColumns(doctype, targetGroup.target_doctype) || 
                  getDefaultColumns();
                const totalCount = targetGroup.count ?? 0;
                // Use edge_id if available (graph-driven), fallback to target_doctype (legacy)
                const groupKey = (targetGroup as RelationshipGraphGroup).edge_id || targetGroup.target_doctype;
                // Check if has_more is available (graph-driven groups)
                const hasMore = (targetGroup as RelationshipGraphGroup).has_more ?? false;
                // Display count with "+" if has_more is true
                const countDisplay = hasMore ? `${totalCount}+` : `${totalCount}`;
                
                return (
                  <div key={groupKey} className="space-y-2">
                    {/* Doctype header - now clickable button */}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-2 py-1 -ml-2"
                        onClick={async () => {
                          const graphGroup = targetGroup as RelationshipGraphGroup;
                          const filters = graphGroup.filters;
                          const resolution = graphGroup.resolution || "query";
                          
                          if (resolution === "query" && filters && filters.length > 0) {
                            // Use backend-provided filters (async for token endpoint)
                            try {
                              const route = await buildListRouteWithFilters(
                                targetGroup.target_doctype,
                                filters as FrappeFilter[]
                              );
                              navigate(route);
                            } catch (error) {
                              console.error("[DocConnectionsTab] Failed to build route:", error);
                              // Fallback to unfiltered list
                              navigate(buildListRoute(targetGroup.target_doctype));
                            }
                          } else {
                            // No filters available - show explicit error + offer unfiltered option
                            const proceed = window.confirm(
                              `List filters are not available for this connection. Open the list view without filters?`
                            );
                            if (proceed) {
                              navigate(buildListRoute(targetGroup.target_doctype));
                            }
                          }
                        }}
                        aria-label={`View all ${targetGroup.target_label || targetGroup.target_doctype}`}
                      >
                        <span>{getDoctypeIcon(targetGroup.target_doctype)}</span>
                        <span className="font-medium">
                          {getDoctypeLabelTranslation(targetGroup.target_doctype, language) || targetGroup.target_label}
                        </span>
                      </button>
                      <Badge variant="secondary">
                        {countDisplay} document{totalCount !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                    
                    {/* Conditional rendering: table or "No documents yet" */}
                    {totalCount === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No documents yet.
                      </p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {columns.map((col) => (
                              <TableHead key={col.field}>{col.label}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {targetGroup.docs.map((doc) => (
                            <TableRow
                              key={doc.name}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => {
                                const route = buildDocRoute(
                                  targetGroup.target_doctype,
                                  doc.name
                                );
                                navigate(route);
                              }}
                            >
                              {columns.map((col) => (
                                <TableCell key={col.field}>
                                  {formatValue(doc[col.field], col.type)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
